import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterHdrOutlinedIcon from '@mui/icons-material/FilterHdrOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {
    AccordionDetails,
    Box,
    Button,
    Card,
    Container,
    Divider,
    Grid,
    MenuItem,
    Accordion as MuiAccordion,
    AccordionSummary as MuiAccordionSummary,
    Stack,
    styled,
} from '@mui/material';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import Dropdown from '/@/components/dropdown';
import Map from '/@/components/map';
import DetailHeader from '/@/components/nitrogen-smart/headers/detail-header';
import Popover from '/@/components/popover';
import Spinner from '/@/components/spinner';
import { useUnitProvider } from '/@/core/context/unit.js';
import { getBounds } from '/@/core/fields';
import settings from '/@/core/settings';
import { useAnchorEl } from '/@/hooks/anchor-el/useAnchorEl';
import { useFieldById } from '/@/hooks/fields';
import { useNitrogenSource, useNSRecommendationById } from '/@/hooks/nitrogen-smart';
import { _ } from '/@/locale';
import { formatDate } from '/@/utils';
import useIsMobile from '/@/utils/hooks/is-mobile';
import Title from '/@/v3/components/title';

/** @param {{language:string,id:string,location:{pathname:string,search:string,hash:string}}} props */
export default function NitrogenRecommendationDetail(props) {
    const { id } = props;
    const [{ open }, { handleClick, handleClose }] = useAnchorEl();
    const mapRef = useRef(null);
    const units = useUnitProvider();
    const { data: nitrogenRecommendation } = useNSRecommendationById(id);
    const [showMap, setShowMap] = useState(false);
    const nitrogenSource = useNitrogenSource((nitrogenRecommendation || {}).nitrogenSource);
    const fieldQuery = useFieldById({ id: nitrogenRecommendation?.field }, { enabled: nitrogenRecommendation?.field });
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!mapRef.current || !fieldQuery.data) return;

        mapRef.current.fitBounds(getBounds(fieldQuery.data.geometry));
    }, [fieldQuery.data, showMap]);

    const [detailHeaderHeight, setDetailHeaderHeight] = useState(0);
    const setDetailHeaderHeightCallback = useCallback(
        /** @param {HTMLDivElement} ref */
        (ref) => {
            if (ref) {
                return setDetailHeaderHeight(ref.clientHeight);
            }
        },
        []
    );
    const containerHeight = `calc(100vh - ${detailHeaderHeight}px - 12rem)`;

    if (showMap && fieldQuery.data)
        return (
            <Grid container sx={{ width: '100%', height: '100%', position: 'relative' }}>
                <Grid
                    item
                    sx={{ px: 2, py: 4, position: 'absolute', height: 'min-content', width: '100%', zIndex: 100 }}
                >
                    <Button
                        variant='contained'
                        onClick={() => {
                            setShowMap(false);
                        }}
                    >
                        {_('Volver a la Recomendación')}
                    </Button>
                </Grid>
                <Grid item>
                    <Map
                        ref={(ref) => (mapRef.current = ref)}
                        fields={[{ ...fieldQuery.data, iconType: 'polygon' }]}
                        zoomPosition='TOP_RIGHT'
                        noGeocoder
                    />
                </Grid>
            </Grid>
        );
    return (
        <Fragment>
            {nitrogenRecommendation ? (
                <Box>
                    <DetailHeader id={id} ref={setDetailHeaderHeightCallback} />
                    <Container maxWidth={isMobile ? false : 'lg'} sx={{ px: 0 }} disableGutters={isMobile}>
                        <Grid container spacing={1} sx={{}}>
                            <Grid
                                item
                                xs={12}
                                md={8}
                                sx={{
                                    overflowY: 'auto',
                                    height: { xs: '100%', md: containerHeight },
                                }}
                            >
                                <Accordion>
                                    <AccordionSummary>{_('Perfil del lote')}</AccordionSummary>
                                    <AccordionDetails>
                                        {[
                                            {
                                                label: _('Nombre'),
                                                value: fieldQuery.data?.name,
                                                Icon: BadgeOutlinedIcon,
                                            },
                                            {
                                                label: _('Ubicación'),
                                                value: fieldQuery.data?.location,
                                                Icon: LocationOnOutlinedIcon,
                                            },
                                            {
                                                label: _('Altitud'),
                                                value: `${Math.round(fieldQuery.data?.altitude)} m`,
                                                Icon: FilterHdrOutlinedIcon,
                                            },
                                        ].map(({ label, value, Icon }, i, arr) => (
                                            <React.Fragment key={label}>
                                                <Grid container spacing={2} sx={{ padding: 2 }}>
                                                    <Grid item xs={isMobile ? 12 : 3}>
                                                        <Title bolder theme={settings.app.theme} size='md'>
                                                            <Icon
                                                                fontSize='large'
                                                                sx={{
                                                                    verticalAlign: 'sub',
                                                                    display: { xs: 'none', md: 'block' },
                                                                }}
                                                            />
                                                            {label}
                                                        </Title>
                                                    </Grid>
                                                    <Grid item sx={{ alignContent: 'center' }}>
                                                        <Title size='md'>{value}</Title>
                                                    </Grid>
                                                </Grid>
                                                {i < arr.length - 1 && (
                                                    <Divider
                                                        orientation='horizontal'
                                                        sx={{ width: '100%', display: { xs: 'none', md: 'block' } }}
                                                    />
                                                )}
                                            </React.Fragment>
                                        ))}
                                        <Box sx={{ p: 2, display: { xs: 'block', md: 'none' } }}>
                                            <Button
                                                variant='outlined'
                                                onClick={() => {
                                                    setShowMap(true);
                                                }}
                                                fullWidth
                                                startIcon={<LocationOnOutlinedIcon />}
                                            >
                                                {_('Ver mapa')}
                                            </Button>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion>
                                    <AccordionSummary>{_('Detalle N-Smart')}</AccordionSummary>
                                    <AccordionDetails>
                                        {[
                                            [
                                                { label: _('Híbrido'), value: nitrogenRecommendation.productName },
                                                {
                                                    label: _('Fecha de siembra'),
                                                    value: formatDate(new Date(nitrogenRecommendation.plantingDate)),
                                                },
                                            ],
                                            [
                                                {
                                                    label: _('Tipo de recomendación'),
                                                    value:
                                                        nitrogenRecommendation.type === 'fixed'
                                                            ? _('Recomendación fija')
                                                            : _('Recomendación variable'),
                                                },
                                                {
                                                    label: _('Fecha estimada de aplicación'),
                                                    value: formatDate(
                                                        new Date(nitrogenRecommendation.estimatedApplicationDate)
                                                    ),
                                                },
                                            ],
                                            [
                                                {
                                                    label: _('Tipo de producción'),
                                                    value:
                                                        nitrogenRecommendation.technique === 'dryland'
                                                            ? _('Secano')
                                                            : _('Riego'),
                                                },
                                                {
                                                    label: _('Fecha definitiva de aplicación'),
                                                    value:
                                                        (nitrogenRecommendation.applicationDate &&
                                                            formatDate(
                                                                new Date(nitrogenRecommendation.applicationDate)
                                                            )) ||
                                                        '-',
                                                },
                                            ],
                                            [{ label: _('Fuente de nitrógeno'), value: nitrogenSource?.name }],
                                        ].map((items, i, arr) => (
                                            <React.Fragment key={i}>
                                                <Grid container spacing={2} sx={{ padding: 2 }}>
                                                    {items.map(({ label, value }) => (
                                                        <React.Fragment key={label}>
                                                            <Grid item xs={isMobile ? 12 : 3}>
                                                                <Title bolder theme={settings.app.theme} size='md'>
                                                                    {label}
                                                                </Title>
                                                            </Grid>
                                                            <Grid
                                                                item
                                                                xs={isMobile ? 12 : 3}
                                                                sx={{ alignContent: 'center' }}
                                                            >
                                                                <Title size='md'>{value}</Title>
                                                            </Grid>
                                                        </React.Fragment>
                                                    ))}
                                                </Grid>
                                                {i < arr.length - 1 && (
                                                    <Divider
                                                        orientation='horizontal'
                                                        sx={{ width: '100%', display: { xs: 'none', md: 'block' } }}
                                                    />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                                {nitrogenRecommendation.managementZones.map((mz, i) => (
                                    <React.Fragment key={mz.id}>
                                        <Accordion>
                                            <AccordionSummary>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        width: '100%',
                                                        pr: 2,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                        }}
                                                    >
                                                        {Boolean(nitrogenRecommendation.managementZoneFiles.length) &&
                                                            isMobile &&
                                                            i === 0 && (
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: 25,
                                                                        height: 25,
                                                                        borderRadius: '50%',
                                                                        bgcolor: (t) => t.palette.primary.main,
                                                                        color: 'white',
                                                                    }}
                                                                >
                                                                    <span
                                                                        className='material-icons'
                                                                        style={{ fontSize: 16, color: 'white' }}
                                                                    >
                                                                        attach_file
                                                                    </span>
                                                                </Box>
                                                            )}
                                                        <span>
                                                            {_('Zona de manejo #')}
                                                            {i + 1}
                                                        </span>
                                                    </Box>

                                                    {Boolean(nitrogenRecommendation.managementZoneFiles.length) &&
                                                        !isMobile && (
                                                            <Dropdown
                                                                ButtonProps={{
                                                                    variant: 'outlined',
                                                                    color: 'primary',
                                                                    disabled:
                                                                        !nitrogenRecommendation.managementZoneFiles
                                                                            .length,
                                                                }}
                                                                MenuProps={{ MenuListProps: { sx: { pb: 0 } } }}
                                                                label={`Archivos adjuntos (${nitrogenRecommendation.managementZoneFiles.length})`}
                                                            >
                                                                {nitrogenRecommendation.managementZoneFiles.map(
                                                                    ({ file }) => {
                                                                        const [name, ext] = file
                                                                            .split('?')[0]
                                                                            .split('/')
                                                                            .pop()
                                                                            .split('.');
                                                                        return (
                                                                            <MenuItem
                                                                                key={file}
                                                                                component='a'
                                                                                download
                                                                                href={file}
                                                                                target='_blank'
                                                                            >
                                                                                {name}.{ext.toUpperCase()}
                                                                            </MenuItem>
                                                                        );
                                                                    }
                                                                )}
                                                                <MenuItem
                                                                    sx={{
                                                                        bgcolor: (t) => t.palette.grey[200],
                                                                    }}
                                                                    onClick={() => {
                                                                        nitrogenRecommendation.managementZoneFiles.forEach(
                                                                            ({ file }, i) => {
                                                                                setTimeout(() => {
                                                                                    const a =
                                                                                        document.createElement('a');
                                                                                    a.setAttribute(
                                                                                        'download',
                                                                                        file
                                                                                            .split('?')[0]
                                                                                            .split('/')
                                                                                            .pop()
                                                                                    );
                                                                                    a.setAttribute('target', '_blank');
                                                                                    a.href = file;
                                                                                    a.click();
                                                                                }, 200 * i);
                                                                            }
                                                                        );
                                                                    }}
                                                                >
                                                                    {_('Descargar todos')}
                                                                </MenuItem>
                                                            </Dropdown>
                                                        )}
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                {[
                                                    { label: _('Nombre'), value: fieldQuery.data?.name },
                                                    {
                                                        label: _('Ambiente'),
                                                        value: (
                                                            <React.Fragment>
                                                                {units.toYield(mz.environment, 'corn').value}&nbsp;
                                                                {units.getUnit('yield', 'corn')}
                                                            </React.Fragment>
                                                        ),
                                                    },
                                                    {
                                                        label: _('Densidad'),
                                                        value: (
                                                            <React.Fragment>
                                                                {units.toDensity(mz.density, 'corn', 2).value}&nbsp;
                                                                {units.getUnit('density', 'corn')}
                                                            </React.Fragment>
                                                        ),
                                                    },
                                                    {
                                                        label: _('Nitrógeno aplicado a la siembra'),
                                                        value: (
                                                            <React.Fragment>
                                                                {mz.appliedNitrogen?.toFixed(2) || '-'}
                                                                &nbsp; kg/ha
                                                            </React.Fragment>
                                                        ),
                                                    },
                                                    {
                                                        label: _('Muestreo de nitrógeno en el suelo'),
                                                        value: (
                                                            <React.Fragment>
                                                                {mz.soilNitrogen?.toFixed(2) || '-'}&nbsp; kg/ha (N)
                                                            </React.Fragment>
                                                        ),
                                                    },
                                                ].map(({ label, value }, i, arr) => (
                                                    <React.Fragment key={label}>
                                                        <Grid container spacing={2} sx={{ padding: 2 }}>
                                                            <Grid item xs={isMobile ? 12 : 3}>
                                                                <Title bolder theme={settings.app.theme} size='md'>
                                                                    {label}
                                                                </Title>
                                                            </Grid>
                                                            <Grid item sx={{ alignContent: 'center' }}>
                                                                <Title size='md'>{value}</Title>
                                                            </Grid>
                                                        </Grid>
                                                        {i < arr.length - 1 && (
                                                            <Divider
                                                                orientation='horizontal'
                                                                sx={{
                                                                    width: '100%',
                                                                    display: { xs: 'none', md: 'block' },
                                                                }}
                                                            />
                                                        )}
                                                    </React.Fragment>
                                                ))}

                                                {Boolean(nitrogenRecommendation.managementZoneFiles.length) &&
                                                    isMobile &&
                                                    i === 0 && (
                                                        <Grid item sx={{ p: 2 }}>
                                                            <Button variant='outlined' onClick={handleClick} fullWidth>
                                                                {_('Archivos adjuntos ')}
                                                                <span>{`(${nitrogenRecommendation.managementZoneFiles.length})`}</span>
                                                            </Button>
                                                        </Grid>
                                                    )}
                                            </AccordionDetails>
                                        </Accordion>
                                    </React.Fragment>
                                ))}
                                <Accordion>
                                    <AccordionSummary>{_('Contacto')}</AccordionSummary>
                                    <AccordionDetails>
                                        {[
                                            {
                                                label: _('Nombre del contacto'),
                                                value: nitrogenRecommendation.distributor
                                                    ? nitrogenRecommendation.distributor
                                                    : '-',
                                            },
                                            {
                                                label: _('Teléfono'),
                                                value: nitrogenRecommendation.phone
                                                    ? nitrogenRecommendation.phone
                                                    : '-',
                                            },
                                            {
                                                label: _('Nombre del productor'),
                                                value: nitrogenRecommendation.companyName
                                                    ? nitrogenRecommendation.companyName
                                                    : '-',
                                            },
                                        ].map(({ label, value }, i, arr) => (
                                            <React.Fragment key={label}>
                                                <Grid container spacing={2} sx={{ padding: 2 }}>
                                                    <Grid item xs={isMobile ? 12 : 3}>
                                                        <Title bolder theme={settings.app.theme} size='md'>
                                                            {label}
                                                        </Title>
                                                    </Grid>
                                                    <Grid item>
                                                        <Title size='md' sx={{ alignContent: 'center' }}>
                                                            {value}
                                                        </Title>
                                                    </Grid>
                                                </Grid>
                                                {i < arr.length - 1 && (
                                                    <Divider
                                                        orientation='horizontal'
                                                        sx={{ width: '100%', display: { xs: 'none', md: 'block' } }}
                                                    />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                            <Grid item xs={4} sm={4} sx={{ display: { xs: 'none', md: 'block' } }}>
                                <Card
                                    sx={{
                                        height: { xs: '100%', md: containerHeight },
                                        width: '100%',
                                        position: 'relative',
                                    }}
                                >
                                    {fieldQuery.isValidating ? (
                                        <Stack
                                            justifyContent='center'
                                            alignItems='center'
                                            width={'100%'}
                                            height={'100%'}
                                        >
                                            <Spinner />
                                        </Stack>
                                    ) : (
                                        <Map
                                            ref={(ref) => (mapRef.current = ref)}
                                            fields={[{ ...fieldQuery.data, iconType: 'polygon' }]}
                                            zoomPosition='TOP_RIGHT'
                                            noGeocoder
                                        />
                                    )}
                                </Card>
                            </Grid>
                        </Grid>
                    </Container>

                    <Popover handleClose={handleClose} open={open && isMobile} title={_('Archivos Adjuntos')}>
                        <Grid item container direction='column' sx={{ gap: 1 }}>
                            {nitrogenRecommendation.managementZoneFiles.map(({ file }) => {
                                const [name, ext] = file.split('?')?.[0]?.split('/').pop().split('.');
                                return (
                                    <Grid item key={file} component='a' download href={file} target='_blank'>
                                        <Button fullWidth variant='outlined'>
                                            {name}.{ext.toUpperCase()}
                                        </Button>
                                    </Grid>
                                );
                            })}

                            <Grid item>
                                <Button
                                    fullWidth
                                    variant='contained'
                                    onClick={() => {
                                        nitrogenRecommendation.managementZoneFiles.forEach(({ file }, i) => {
                                            setTimeout(() => {
                                                const a = document.createElement('a');
                                                a.setAttribute('download', file.split('?')?.[0]?.split('/').pop());
                                                a.setAttribute('target', '_blank');
                                                a.href = file;
                                                a.click();
                                            }, 200 * i);
                                        });
                                    }}
                                >
                                    {_('Descargar todos')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Popover>
                </Box>
            ) : (
                <Box width='100%' height='100%' display='flex'>
                    <Box mx='auto' my='auto'>
                        <Spinner />
                    </Box>
                </Box>
            )}
        </Fragment>
    );
}

const Accordion = styled((props) => <MuiAccordion {...props} elevation={0} variant='outlined' defaultExpanded />)(
    ({ theme }) => ({
        marginTop: '0!important',
        marginBottom: '0!important',
        border: `1px solid ${theme.palette.divider}`,
        '&:not(:last-child)': {
            borderBottom: 0,
        },
    })
);

const AccordionSummary = (props) => (
    <MuiAccordionSummary expandIcon={<ExpandMoreIcon fontSize='large' color='primary' />} {...props} />
);
